<template>
    <div class="member-map">
        <div class="member-map__search">
            <form @submit.prevent="search" class="filter-form">
                <fieldset>
                    <div class="field">
                        <input
                            v-model="query"
                            type="text"
                            placeholder="PLZ oder Stadt eingeben"
                        />
                    </div>
                    <div class="field">
                        <input type="submit" value="Suchen" />
                    </div>
                    <div v-if="searchLocation || zipPrefix" class="field">
                        <button type="button" class="btn--reset" @click="resetSearch">Zurücksetzen</button>
                    </div>
                </fieldset>
            </form>
            <div class="member-map__types">
                <label><input type="checkbox" v-model="filterDoctor" /> Arzt/Ärztin</label>
                <label><input type="checkbox" v-model="filterTherapist" /> Therapeut/Therapeutin</label>
            </div>
            <p>{{ filteredMembers.length }} Mitglieder gefunden<span v-if="searchLocation || zipPrefix"> für &bdquo;{{ searchLabel }}&ldquo;</span></p>
        </div>

        <div ref="mapContainer" class="member-map__map"></div>

        <div class="member-map__list">
            <div
                v-for="member in filteredMembers"
                :key="member.id"
                :id="'member-' + member.id"
                class="member"
                :class="{ 'member--open': openMemberId === member.id }"
            >
                <div class="member_head">
                    <h4>
                        <a href="#" @click.prevent="flyTo(member)">{{ member.title }}</a>
                    </h4>
                    <p v-if="member.organization">{{ member.organization }}</p>
                    <p v-if="member.targetGroups || member.specialization">
                        <template v-if="member.targetGroups">{{ member.targetGroups }}</template>
                        <template v-if="member.targetGroups && member.specialization"><br/></template>
                        <template v-if="member.specialization">{{ member.specialization }}</template>
                    </p>
                    <p v-if="member.distance != null" class="member_distance">
                        ca. {{ Math.round(member.distance) }} km entfernt
                    </p>
                    <div class="member_more">
                        <a href="#" class="link--more" @click.prevent="toggleMember(member.id)">Kontaktinfos</a>
                    </div>
                </div>
                <div class="member_content">
                    <div class="member_text">
                        <p>
                            <template v-if="member.email">
                                {{ member.emailLabel || 'E-Mail' }}: <a :href="'mailto:' + member.email">{{ member.email }}</a><br/>
                            </template>
                            <template v-if="member.tel">Telefon: {{ member.tel }}<br/></template>
                            <template v-if="member.fax">Fax: {{ member.fax }}<br/></template>
                            <template v-if="member.website">Website: <a :href="member.website" target="_blank">{{ member.website }}</a></template>
                        </p>
                    </div>
                </div>
                <div class="member_footer">
                    <p>
                        <template v-if="member.street">{{ member.street }}<br/></template>
                        {{ member.zip }} {{ member.city }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import maplibregl from 'maplibre-gl'

const TILE_STYLE_URL = 'https://www.goodmaps.de/map-styles/8d8bf301d9d530613f96f2c42b47551a'

function haversineDistance(lat1, lng1, lat2, lng2) {
    const R = 6371
    const dLat = (lat2 - lat1) * Math.PI / 180
    const dLng = (lng2 - lng1) * Math.PI / 180
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2)
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

export default {
    props: {
        membersUrl: { type: String, required: true },
    },

    data() {
        return {
            members: [],
            query: '',
            searchLocation: null,
            searchLabel: '',
            zipPrefix: null,
            openMemberId: null,
            map: null,
            markers: [],
            filterDoctor: true,
            filterTherapist: true,
        }
    },

    computed: {
        filteredMembers() {
            let list = this.members
                .filter(m => this.typeSelected(m))
                .map(m => ({
                    ...m,
                    distance: this.searchLocation && m.lat && m.lng
                        ? haversineDistance(this.searchLocation.lat, this.searchLocation.lng, m.lat, m.lng)
                        : null,
                }))

            if (this.zipPrefix) {
                list = list.filter(m => m.zip && m.zip.startsWith(this.zipPrefix))
            }

            if (this.searchLocation) {
                list.sort((a, b) => (a.distance ?? Infinity) - (b.distance ?? Infinity))
            }

            return list
        },
    },

    watch: {
        filterDoctor() { this.addMarkers() },
        filterTherapist() { this.addMarkers() },
    },

    async mounted() {
        await this.loadMembers()
        this.initMap()
    },

    methods: {
        async loadMembers() {
            const response = await fetch(this.membersUrl)
            this.members = await response.json()
        },

        typeSelected(member) {
            const isDoctor = (member.professionType || 'doctor') === 'doctor'
            return isDoctor ? this.filterDoctor : this.filterTherapist
        },

        initMap() {
            this.map = new maplibregl.Map({
                container: this.$refs.mapContainer,
                style: TILE_STYLE_URL,
                center: [10.0, 51.5],
                zoom: 4.5,
            })

            this.map.addControl(new maplibregl.NavigationControl(), 'top-right')

            this.map.on('load', () => {
                this.addMarkers()
            })
        },

        addMarkers() {
            if (!this.map) return

            this.markers.forEach(m => m.remove())
            this.markers = []

            // Group members by location (respektiert den Berufsgruppen-Filter)
            const groups = {}
            this.members.filter(member => this.typeSelected(member)).forEach(member => {
                if (!member.lat || !member.lng) return
                const key = `${member.lat.toFixed(4)},${member.lng.toFixed(4)}`
                if (!groups[key]) {
                    groups[key] = { lat: member.lat, lng: member.lng, members: [] }
                }
                groups[key].members.push(member)
            })

            Object.values(groups).forEach(group => {
                const popupHtml = group.members.map(member =>
                    `<div class="marker-popup__member">` +
                    `<strong><a href="#member-${member.id}" class="marker-popup__link">${this.escapeHtml(member.title)}</a></strong>` +
                    (member.organization ? `<br/>${this.escapeHtml(member.organization)}` : '') +
                    `<br/>${this.escapeHtml(member.zip)} ${this.escapeHtml(member.city)}` +
                    `</div>`
                ).join('<hr/>')

                const popup = new maplibregl.Popup({ offset: 25, maxWidth: '300px' }).setHTML(popupHtml)
                popup.on('open', () => {
                    this.panToShowPopup(popup)
                    popup.getElement().querySelectorAll('.marker-popup__link').forEach(link => {
                        link.addEventListener('click', (e) => {
                            e.preventDefault()
                            const target = document.querySelector(link.getAttribute('href'))
                            if (target) {
                                target.scrollIntoView({ behavior: 'smooth', block: 'center' })
                                const memberId = parseInt(link.getAttribute('href').replace('#member-', ''))
                                this.openMemberId = memberId
                            }
                        })
                    })
                })

                const marker = new maplibregl.Marker()
                    .setLngLat([group.lng, group.lat])
                    .setPopup(popup)
                    .addTo(this.map)

                this.markers.push(marker)
            })
        },

        panToShowPopup(popup) {
            const popupEl = popup.getElement()
            if (!popupEl) return

            this.$nextTick(() => {
                const mapContainer = this.map.getContainer()
                const mapRect = mapContainer.getBoundingClientRect()
                const popupRect = popupEl.getBoundingClientRect()
                const padding = 20

                let dx = 0
                let dy = 0

                if (popupRect.top < mapRect.top + padding) {
                    dy = popupRect.top - mapRect.top - padding
                } else if (popupRect.bottom > mapRect.bottom - padding) {
                    dy = popupRect.bottom - mapRect.bottom + padding
                }

                if (popupRect.left < mapRect.left + padding) {
                    dx = popupRect.left - mapRect.left - padding
                } else if (popupRect.right > mapRect.right - padding) {
                    dx = popupRect.right - mapRect.right + padding
                }

                if (dx !== 0 || dy !== 0) {
                    this.map.panBy([dx, dy], { duration: 300 })
                }
            })
        },

        escapeHtml(text) {
            if (!text) return ''
            const div = document.createElement('div')
            div.textContent = text
            return div.innerHTML
        },

        async search() {
            const trimmed = this.query.trim()
            if (!trimmed) return

            // Partial PLZ (1-4 digits): filter members by prefix and fit map bounds
            if (/^\d{1,4}$/.test(trimmed)) {
                this.searchLocation = null
                this.zipPrefix = trimmed
                this.searchLabel = 'PLZ ' + trimmed + '…'

                this.$nextTick(() => {
                    this.fitToFilteredMembers()
                })
                return
            }

            // Full PLZ or city name: use Nominatim
            this.zipPrefix = null

            const params = new URLSearchParams({
                q: trimmed,
                format: 'json',
                limit: '1',
                countrycodes: 'de,at,ch',
                'accept-language': 'de',
            })

            const response = await fetch(`https://nominatim.openstreetmap.org/search?${params}`)
            const data = await response.json()

            if (data.length === 0) {
                return
            }

            this.searchLocation = {
                lat: parseFloat(data[0].lat),
                lng: parseFloat(data[0].lon),
            }
            this.searchLabel = trimmed

            this.map.flyTo({
                center: [this.searchLocation.lng, this.searchLocation.lat],
                zoom: 8,
            })
        },

        resetSearch() {
            this.query = ''
            this.searchLocation = null
            this.searchLabel = ''
            this.zipPrefix = null
            this.map.flyTo({
                center: [10.0, 51.5],
                zoom: 5,
            })
        },

        fitToFilteredMembers() {
            const withCoords = this.filteredMembers.filter(m => m.lat && m.lng)
            if (withCoords.length === 0) return

            const bounds = new maplibregl.LngLatBounds()
            withCoords.forEach(m => bounds.extend([m.lng, m.lat]))

            this.map.fitBounds(bounds, { padding: 50, maxZoom: 12 })
        },

        flyTo(member) {
            if (!member.lat || !member.lng) return
            this.map.flyTo({
                center: [member.lng, member.lat],
                zoom: 13,
            })

            const marker = this.markers.find(m => {
                const lngLat = m.getLngLat()
                return Math.abs(lngLat.lat - member.lat) < 0.0001 &&
                       Math.abs(lngLat.lng - member.lng) < 0.0001
            })
            if (marker) {
                marker.togglePopup()
            }
        },

        toggleMember(id) {
            this.openMemberId = this.openMemberId === id ? null : id
        },
    },
}
</script>
